import React from "react";
import { Card } from "@/components/ui/card";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { cn } from "@/lib/utils";


export interface DecisionGroup {
  operationalOrganizationId: number;
  operationalOrganizationShortName: string;
}

export interface DecisionGroupCirclesProps {
  groups?: DecisionGroup[];
  onGroupClick?: (_group: DecisionGroup) => void;
  size?: "default" | "medium" | "small";
  useLongName?: boolean;
}

const generateColourFromId = (id: number): string => {
  const colours = [
    "#BA68C8", "#9575CD", "#7986CB", "#64B5F6", "#4FC3F7"
  ];
  return colours[id % colours.length];
};

/**
 * Get the abbreviation of a group's short name.
 *
 * This function takes a group's short name and returns its abbreviation.
 * If the short name contains spaces, it takes the first two words and
 * takes the first letter of each word.
 *
 * @param {string} shortName - The short name of the group.
 * @returns {string} The abbreviation of the group's short name.
 */
const getGroupAbbreviation = (shortName: string): string => {
  return shortName.includes(" ")
    ? shortName.split(" ")
      .slice(0, 2)
      .map(word => word.charAt(0).toUpperCase())
      .join("")
    : shortName.charAt(0).toUpperCase();
};

/**
 * DecisionGroupCircles component renders circles representing bid or margin decision groups.
 *
 * This component accepts an array of decision group objects which must include the operationalOrganizationId
 * and operationalOrganizationShortName properties. It then filters out duplicate organizations and renders a
 * circle for each unique organization.
 *
 * @component
 * @param {Object[]} groups - Array of decision group objects. Each object should have an operationalOrganizationId and operationalOrganizationShortName.
 * @param {Function} onGroupClick - Optional callback function that is called when a group circle is clicked.
 * @param {string} size - Size of the circles, either "default", "medium", or "small".
 * @returns {JSX.Element} A set of circles representing the decision groups.
 */
export const DecisionGroupCircles: React.FC<DecisionGroupCirclesProps> = ({
  groups = [],
  onGroupClick,
  size = "default",
  useLongName = false
}) => {
  // Filter unique groups based on operationalOrganizationId and sort them by ID
  const uniqueGroups = [...new Map(groups.map(group => [group.operationalOrganizationId, group])).values()]
    .sort((a, b) => a.operationalOrganizationId - b.operationalOrganizationId);

  // Determine size-based classes
  let circleClasses = "w-8 h-8 text-sm border-2"; // default
  if (size === "medium") {
    circleClasses = "w-6 h-6 text-xs border";
  } else if (size === "small") {
    circleClasses = "w-5 h-5 text-xs border";
  }

  let spacingClass = "-space-x-2"; // default
  if (size === "medium") {
    spacingClass = "-space-x-1.5";
  } else if (size === "small") {
    spacingClass = "-space-x-1";
  }

  // Determine border width based on size
  const getBorderStyle = () => {
    if (size === "medium") {
      return { borderWidth: "1.5px" };
    } else if (size === "small") {
      return { borderWidth: "1px" };
    }
    return {}; // default uses Tailwind's border-2 class
  };

  // Show 4 circles when there are exactly 4 groups, otherwise show 3 + +N
  const shouldShowPlusCircle = uniqueGroups.length > 4;
  const visibleGroups = shouldShowPlusCircle
    ? uniqueGroups.slice(0, 3)
    : uniqueGroups.slice(0, 4);
  const remainingGroups = shouldShowPlusCircle
    ? uniqueGroups.slice(3)
    : [];
  const hasRemainingGroups = remainingGroups.length > 0;

  return (
    <div className={cn("flex", spacingClass)}>
      {visibleGroups.map(group => (
        <div
          key={group.operationalOrganizationId}
          className={cn(
            "group relative rounded-full flex items-center justify-center text-white border-white",
            useLongName && "w-auto px-1",
            onGroupClick && "cursor-pointer",
            circleClasses
          )}
          style={{
            backgroundColor: generateColourFromId(group.operationalOrganizationId),
            ...getBorderStyle()
          }}
          onClick={onGroupClick ? (e) => { e.stopPropagation(); onGroupClick(group); } : undefined}
        >
          {
            useLongName ? (
              group.operationalOrganizationShortName
            ) : (
              getGroupAbbreviation(group.operationalOrganizationShortName)
            )
          }
          <Card className="hidden absolute -bottom-8 right-0 w-fit px-2 py-1 text-sm group-hover:block z-10">
            <p className="text-center text-slate-800 py-1 whitespace-nowrap">
              {group.operationalOrganizationShortName}
            </p>
          </Card>
        </div>
      ))}

      {hasRemainingGroups && (
        <HoverCard>
          <HoverCardTrigger asChild>
            <div
              className={cn(
                "group relative rounded-full flex items-center justify-center text-white border-white bg-slate-400 cursor-pointer",
                circleClasses
              )}
              style={getBorderStyle()}
              onClick={(e) => e.stopPropagation()}
            >
              +{remainingGroups.length}
            </div>
          </HoverCardTrigger>
          <HoverCardContent className="p-2 w-fit">
            <div>
              {remainingGroups.map(group => (
                <div
                  key={group.operationalOrganizationId}
                  className="flex items-center gap-2 cursor-pointer hover:bg-slate-100 p-2 rounded"
                  onClick={onGroupClick ? (e) => { e.stopPropagation(); onGroupClick(group); } : undefined}
                >
                  <div
                    className={cn("rounded-full flex items-center justify-center text-white border-white", circleClasses)}
                    style={{
                      backgroundColor: generateColourFromId(group.operationalOrganizationId),
                      ...getBorderStyle()
                    }}
                  >
                    {getGroupAbbreviation(group.operationalOrganizationShortName)}
                  </div>
                  <span className="text-sm text-slate-800">
                    {group.operationalOrganizationShortName}
                  </span>
                </div>
              ))}
            </div>
          </HoverCardContent>
        </HoverCard>
      )}
    </div>
  );
};
